import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsCheckCircleFill, BsXCircleFill, BsInfoCircle, BsChevronDown, BsChevronUp } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

export default function TermsAcceptanceModal({ isOpen, onClose, onAccept }) {
	const [accepted, setAccepted] = useState(false);
	const [showTips, setShowTips] = useState(false);

	const handleAccept = () => {
		if (accepted && onAccept) {
			onAccept();
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="l">
			<Panel.Header
				onClose={onClose}
				subtitle="Schnellstart-Anleitung für erfolgreiche Unternehmensdatenrecherche"
			>
				Willkommen bei B2B Hero
			</Panel.Header>

			<Panel.Body>
				{/* Dos and Don'ts */}
				<div className="mb-6">
					<h3 className="text-lg font-bold mb-4 flex items-center gap-2">
						<BsInfoCircle className="text-primary-700" />
						Grundregeln für E-Mail-Kontakte
					</h3>

					<div className="grid md:grid-cols-2 gap-4 mb-4">
						{/* Erlaubt */}
						<div className="bg-success-200 border-2 border-success-500/40 rounded-card p-4">
							<h4 className="text-lg font-semibold text-success-700 mb-3 flex items-center gap-2">
								<BsCheckCircleFill className="text-success-700" />
								ERLAUBT
							</h4>
							<ul className="space-y-2 text-sm text-text">
								<li className="flex items-start gap-2">
									<BsCheckCircleFill className="text-success-700 mt-0.5 flex-shrink-0" size={14} />
									<span>Gezielte, individuelle Ansprache von Unternehmen</span>
								</li>
								<li className="flex items-start gap-2">
									<BsCheckCircleFill className="text-success-700 mt-0.5 flex-shrink-0" size={14} />
									<span>Verwendung der Daten für eigene gewerbliche Zwecke</span>
								</li>
								<li className="flex items-start gap-2">
									<BsCheckCircleFill className="text-success-700 mt-0.5 flex-shrink-0" size={14} />
									<span>Professionelle B2B-Kommunikation</span>
								</li>
							</ul>
						</div>

						{/* Verboten */}
						<div className="bg-warning-200 border-2 border-warning-500/40 rounded-card p-4">
							<h4 className="text-lg font-semibold text-warning-700 mb-3 flex items-center gap-2">
								<BsXCircleFill className="text-warning-700" />
								VERBOTEN
							</h4>
							<ul className="space-y-2 text-sm text-text">
								<li className="flex items-start gap-2">
									<BsXCircleFill className="text-warning-700 mt-0.5 flex-shrink-0" size={14} />
									<span>Massen-E-Mails oder Spam-Nachrichten</span>
								</li>
								<li className="flex items-start gap-2">
									<BsXCircleFill className="text-warning-700 mt-0.5 flex-shrink-0" size={14} />
									<span>Newsletter-Kampagnen über die Plattform</span>
								</li>
								<li className="flex items-start gap-2">
									<BsXCircleFill className="text-warning-700 mt-0.5 flex-shrink-0" size={14} />
									<span>Weitergabe der Daten an Dritte</span>
								</li>
							</ul>
						</div>
					</div>
				</div>

				{/* Pflichtangaben */}
				<div className="mb-6 bg-primary-100 border-2 border-primary-200 rounded-card p-4">
					<h3 className="text-lg font-bold mb-3 text-primary-700">Pflichtangaben in jeder E-Mail</h3>
					<ul className="space-y-2 text-sm text-text">
						<li className="flex items-start gap-2">
							<span className="font-semibold text-primary-700 flex-shrink-0">•</span>
							<span>
								<strong>Impressum:</strong> Vollständige Unternehmensangaben
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="font-semibold text-primary-700 flex-shrink-0">•</span>
							<span>
								<strong>Datenschutzhinweis:</strong> Verweis auf Ihre Datenschutzerklärung
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="font-semibold text-primary-700 flex-shrink-0">•</span>
							<span>
								<strong>Zweck der Kontaktaufnahme:</strong> Transparent kommunizieren
							</span>
						</li>
					</ul>
				</div>

				{/* Rechtliche Grundlagen */}
				<div className="mb-6">
					<h3 className="text-lg font-bold mb-3">Rechtliche Grundlagen beachten</h3>
					<ul className="space-y-2 text-sm text-text">
						<li className="flex items-start gap-2">
							<span className="font-semibold text-primary-600 flex-shrink-0">•</span>
							<span>
								<strong>DSGVO-Konformität:</strong> Nur öffentlich verfügbare Daten verwenden
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="font-semibold text-primary-600 flex-shrink-0">•</span>
							<span>
								<strong>Wettbewerbsrecht:</strong> Unlauteren Wettbewerb vermeiden
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="font-semibold text-primary-600 flex-shrink-0">•</span>
							<span>
								<strong>Dokumentationspflicht:</strong> Nachweis der rechtmäßigen Datenverwendung
							</span>
						</li>
					</ul>
				</div>

				{/* Praktische Tipps (ausklappbar) */}
				<div className="mb-6 border border-bgBase-500 rounded-card">
					<button
						type="button"
						onClick={() => setShowTips(!showTips)}
						className="w-full flex items-center justify-between p-4 hover:bg-bgBase-300 transition-colors"
					>
						<h3 className="text-lg font-semibold text-text">Praktische Tipps für erfolgreiche Kontakte</h3>
						{showTips ? (
							<BsChevronUp className="text-text" size={20} />
						) : (
							<BsChevronDown className="text-text" size={20} />
						)}
					</button>

					{showTips && (
						<div className="px-4 pb-4 space-y-4 text-sm text-text">
							<div>
								<h4 className="font-semibold text-text mb-2">1. Vorbereitung</h4>
								<ul className="space-y-1 ml-4">
									<li>• Zielgruppe klar definieren</li>
									<li>• Nutzenversprechen formulieren</li>
									<li>• Personalisierte Ansprache vorbereiten</li>
								</ul>
							</div>
							<div>
								<h4 className="font-semibold text-text mb-2">2. Erste Kontaktaufnahme</h4>
								<ul className="space-y-1 ml-4">
									<li>• Kurze, prägnante Nachrichten</li>
									<li>• Konkreten Mehrwert kommunizieren</li>
									<li>• Call-to-Action einbauen</li>
									<li>• Professionelle E-Mail-Signatur</li>
								</ul>
							</div>
							<div>
								<h4 className="font-semibold text-text mb-2">3. Follow-up Strategie</h4>
								<ul className="space-y-1 ml-4">
									<li>• Angemessene Wartezeiten einhalten</li>
									<li>• Nicht mehr als 2-3 Kontaktversuche</li>
									<li>• Bei Absagen respektieren und dokumentieren</li>
								</ul>
							</div>
							<div className="pt-3 border-t">
								<h4 className="font-semibold text-warning-700 mb-2">Häufige Fehler vermeiden</h4>
								<ul className="space-y-1 ml-4">
									<li>• Massenhafte, unpersonalisierte Ansprache</li>
									<li>• Fehlende Opt-Out-Möglichkeiten</li>
									<li>• Zu aggressive Ansprache</li>
									<li>• Zu häufige Kontaktversuche</li>
								</ul>
							</div>
						</div>
					)}
				</div>

				{/* Support */}
				<div className="bg-bgBase-300 rounded-card p-4 text-sm text-text">
					<p className="mb-2">
						<strong>Support und Hilfe:</strong>
					</p>
					<p className="mb-1">• E-Mail: info@b2bhero.de</p>
					<p className="mb-3">• Website: www.b2bhero.de</p>
					<p className="text-xs text-bgBase-800">
						© 2025 B2B Hero. Alle Rechte vorbehalten.
						<br />
						<strong>Wichtiger Hinweis:</strong> Die Plattform richtet sich ausschließlich an Unternehmer für
						gewerbliche Zwecke.
					</p>
				</div>
			</Panel.Body>

			<Panel.Footer stacked>
				<div className="flex items-start gap-3">
					<Checkbox id="terms-acceptance-checkbox" checked={accepted} onChange={setAccepted} />
					<label htmlFor="terms-acceptance-checkbox" className="text-sm text-text cursor-pointer">
						Ich habe die Nutzungsbedingungen gelesen und akzeptiere die Grundregeln für die Verwendung der
						Plattform. Ich verpflichte mich, die DSGVO-Vorgaben einzuhalten und keine Massen-E-Mails oder
						Spam zu versenden.
					</label>
				</div>

				<div className="flex gap-3 justify-end">
					<Button variant="outline" size="sm" onClick={onClose}>
						Abbrechen
					</Button>
					<Button variant="solid" size="sm" onClick={handleAccept} disabled={!accepted}>
						Akzeptieren und fortfahren
					</Button>
				</div>
			</Panel.Footer>
		</Modal>
	);
}

TermsAcceptanceModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onAccept: PropTypes.func,
};
